import React, { useState, useEffect, useRef } from "react";
import { readPolicies, type PolicyDocument } from '@/lib/auth/policies';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { signup, signin, resendCode, verifyEmail, normalizePhone, AuthApiError, MINIMUM_PASSWORD_LENGTH, socialProviders, googleSignin, telegramSignin, requestSigninCode, socialSignup } from '@/lib/auth/api';
import type { SocialProviders, SocialSignupOffer, AccountIdentity } from '@/lib/auth/api';
import { createTelegramLogin, prepareGoogle, requestGoogleCode } from '@/lib/auth/social';
import GoogleContinueButton from "./components/GoogleContinueButton";
import AuthHeader from './components/AuthHeader';
import AuthPasswordInput from './components/AuthPasswordInput';
import { AUTH_ICON_BTN, AUTH_INPUT, AUTH_OUTLINE_BTN, AUTH_PRIMARY_BTN, AUTH_TEXT_BTN, AUTH_TITLE } from './components/authStyles';
import CountryFlag from '@/components/common/CountryFlag';
import { useLanguage } from '@/i18n/useLanguage';
import PlanSelectionStep from "./components/PlanSelectionStep";
import FieldMessage from './components/FieldMessage';
import PolicyDialog from './components/PolicyDialog';
import DemoAccounts from './components/DemoAccounts';
import { demoSignin } from '@/helpers/authApi';

interface AuthPageProps {
  initialMode?: "login" | "signup";
}

// ----------------- CONFIG DATA WITH TRILINGUAL SUPPORT -----------------
const COUNTRY_CODES = [
  { code: "+855", iso: "KH", name: "Cambodia", nameKm: "កម្ពុជា", nameZh: "柬埔寨" },
  { code: "+1", iso: "US", name: "United States", nameKm: "អាមេរិក", nameZh: "美国" },
  { code: "+86", iso: "CN", name: "China", nameKm: "ចិន", nameZh: "中国" },
  { code: "+66", iso: "TH", name: "Thailand", nameKm: "ថៃ", nameZh: "泰国" },
  { code: "+84", iso: "VN", name: "Vietnam", nameKm: "វៀតណាម", nameZh: "越南" },
  { code: "+65", iso: "SG", name: "Singapore", nameKm: "សិង្ហបុរី", nameZh: "新加坡" },
  { code: "+60", iso: "MY", name: "Malaysia", nameKm: "ម៉ាឡេស៊ី", nameZh: "马来西亚" },
];

const BUSINESS_TYPES = [
  { id: "retail", labelEn: "Retail & E-commerce", labelKm: "លក់រាយ និង E-commerce", labelZh: "零售与电子商务" },
  { id: "hospitality", labelEn: "Restaurant & Hospitality", labelKm: "ភោជនីយដ្ឋាន និងបដិសណ្ឋារកិច្ច", labelZh: "餐饮与酒店住宿" },
  { id: "services", labelEn: "Corporate Services", labelKm: "សេវាកម្មទូទៅ", labelZh: "企业与专业服务" },
  { id: "fintech", labelEn: "Banking & Financial Services", labelKm: "ហិរញ្ញវត្ថុ និងធនាគារ", labelZh: "金融与银行服务" },
  { id: "tech", labelEn: "Technology & Software", labelKm: "បច្ចេកវិទ្យា និង Software", labelZh: "科技与软件开发" },
  { id: "ngo", labelEn: "Non-Profit / NGO", labelKm: "អង្គការ / NGO", labelZh: "非营利组织 / NGO" },
  { id: "other", labelEn: "Other Business Entity", labelKm: "អាជីវកម្មផ្សេងៗ", labelZh: "其他商业主体" },
];

// Comprehensive UI Dictionary (EN default, KM, ZH)
const I18N = {
  en: {
    finishSignupTitle: "Finish signing up",
    signingUpWith: "Signing up with",
    finishGoogleHint: "Add your phone number and accept the terms.",
    finishTelegramHint: "Add your work email and phone number. We will email you a code.",
    loginTitle: "Log in to LoyaltyBridge",
    orDivider: "OR",
    continueWithTelegram: "Continue with Telegram",
    emailPlaceholder: "Email address",
    continueBtn: "Continue",
    checkInboxTitle: "Check your inbox",
    checkInboxBefore: "Enter the verification code we just sent to ",
    checkInboxAfter: "",
    codeLabel: "Code",
    resendEmail: "Resend email",
    continueWithPassword: "Continue with password",
    enterPasswordTitle: "Enter your password",
    codeLenError: "Please enter the complete 6-digit code",
    socialUnavailable: "This sign-in method isn't available yet",
    socialFailed: "Sign-in failed. Please try again.",
    googleNotLinked: "No active account uses this Google email. Please sign up first.",
    telegramNotLinked: "This Telegram isn't connected to an account. Log in another way, then connect it on your account page.",
    brandSubtitle: "Enterprise Rewards & Loyalty",
    home: "Home",
    signUpTitle: "Create Enterprise Account",
    password: "Password",
    passwordPlaceholder: "Enter password",
    passwordMinHint: "At least 15 characters",
    forgotPassword: "Forgot password?",
    verificationCode: "Verification Code",
    codePlaceholder: "6-digit code",
    getCode: "Get Code",
    agreeTo: "I agree to ",
    and: " & ",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    continueWithGoogle: "Continue with Google",
    dontHaveAccount: "Don't have an account?",
    signUpLink: "Sign Up",
    alreadyHaveAccount: "Already have an account?",
    signInLink: "Sign In",
    workEmail: "Work email",
    phoneNumber: "Phone number",
    confirmPassword: "Confirm password",
    match: "✓ Match",
    mismatch: "✗ Mismatch",
    back: "Back",
    companyName: "Company / Organization name",
    businessType: "Business type",
    licenceNumber: "Registration / Licence #",
    uploadLicence: "Upload licence (JPG, PNG, PDF)",
    fileFormats: "JPG, PNG or PDF (Max 10MB)",
    agreeAdRules: "I agree to advertising rules",
    completeReg: "Complete Registration ✓",
    proceedDashboard: "Proceed to Dashboard →",
    validEmailError: "Please enter a valid work email",
    phoneError: "Please enter your phone number",
    pwdLenError: "Password must be at least 15 characters",
    pwdMatchError: "Passwords do not match",
    termsError: "Please agree to Terms of Service & Privacy Policy",
    otpLenError: "Enter all six digits from your email",
    companyError: "Please enter company name",
    licenceError: "Please enter registration/licence number",
    uploadError: "Please upload your business registration licence",
    adRulesError: "Please accept advertising rules",
    fillAllError: "Please fill in phone/email, password, and verification code",
    phoneFirst: "Enter your email and password first",
    codeSent: "Verification code sent to your email",
    fileTypeError: "Only JPG, PNG or PDF files are allowed",
    fileSizeError: "File size must be under 10MB",
    choosePlanTitle: "Choose Your Enterprise Plan",
    choosePlanSubtitle: "Select the edition that fits your business scale. You can upgrade anytime.",
    planStandard: "Standard",
    planAdvanced: "Advanced",
    planFlagship: "Flagship",
    planVip: "Exclusive VIP",
    planSubStandard: "★ Essential Business Plan",
    planSubAdvanced: "★ Includes all Standard features",
    planSubFlagship: "★ Includes all Advanced features",
    planSubVip: "★ Includes Flagship features",
    recommendedRibbon: "👍 70% Recommended",
    selectPlan: "Select Plan",
    skipToDashboard: "Skip & Explore Dashboard →",
    originalPrice: "Original",
    customEnterpriseContact: "Need custom corporate deployment, ERP integration or enterprise SLA? Contact Sales",
  },
  km: {
    finishSignupTitle: "បញ្ចប់ការចុះឈ្មោះ",
    signingUpWith: "ចុះឈ្មោះជាមួយ",
    finishGoogleHint: "បន្ថែមលេខទូរស័ព្ទ ហើយយល់ព្រមលើលក្ខខណ្ឌ។",
    finishTelegramHint: "បន្ថែមអ៊ីមែលការងារ និងលេខទូរស័ព្ទ។ យើងនឹងផ្ញើកូដតាមអ៊ីមែល។",
    loginTitle: "ចូលគណនី LoyaltyBridge",
    orDivider: "ឬ",
    continueWithTelegram: "បន្តជាមួយ Telegram",
    emailPlaceholder: "អាសយដ្ឋានអ៊ីមែល",
    continueBtn: "បន្ត",
    checkInboxTitle: "ពិនិត្យប្រអប់អ៊ីមែលរបស់អ្នក",
    checkInboxBefore: "បញ្ចូលលេខកូដផ្ទៀងផ្ទាត់ដែលយើងទើបផ្ញើទៅ ",
    checkInboxAfter: "",
    codeLabel: "លេខកូដ",
    resendEmail: "ផ្ញើអ៊ីមែលឡើងវិញ",
    continueWithPassword: "បន្តដោយប្រើពាក្យសម្ងាត់",
    enterPasswordTitle: "បញ្ចូលពាក្យសម្ងាត់របស់អ្នក",
    codeLenError: "សូមបញ្ចូលលេខកូដ ៦ ខ្ទង់ឱ្យគ្រប់",
    socialUnavailable: "វិធីចូលនេះមិនទាន់អាចប្រើបានទេ",
    socialFailed: "ការចូលមិនបានជោគជ័យ។ សូមព្យាយាមម្តងទៀត។",
    googleNotLinked: "គ្មានគណនីសកម្មប្រើអ៊ីមែល Google នេះទេ។ សូមចុះឈ្មោះជាមុនសិន។",
    telegramNotLinked: "Telegram នេះមិនទាន់បានភ្ជាប់នឹងគណនីទេ។ សូមចូលតាមវិធីផ្សេង រួចភ្ជាប់នៅទំព័រគណនីរបស់អ្នក។",
    brandSubtitle: "ប្រព័ន្ធពិន្ទុរង្វាន់អាជីវកម្ម",
    home: "ទំព័រដើម",
    signUpTitle: "ចុះឈ្មោះគណនីថ្មី",
    password: "ពាក្យសម្ងាត់",
    passwordPlaceholder: "បញ្ចូលពាក្យសម្ងាត់",
    passwordMinHint: "យ៉ាងតិច ១៥ តួអក្សរ",
    forgotPassword: "ភ្លេចពាក្យសម្ងាត់?",
    verificationCode: "លេខកូដផ្ទៀងផ្ទាត់",
    codePlaceholder: "លេខកូដ ៦ ខ្ទង់",
    getCode: "យកកូដ",
    agreeTo: "ខ្ញុំយល់ព្រមលើ ",
    and: " និង ",
    terms: "លក្ខខណ្ឌសេវាកម្ម",
    privacy: "គោលការណ៍ភាពឯកជន",
    continueWithGoogle: "បន្តជាមួយ Google",
    dontHaveAccount: "មិនទាន់មានគណនី?",
    signUpLink: "ចុះឈ្មោះ (Sign Up)",
    alreadyHaveAccount: "មានគណនីរួចហើយ?",
    signInLink: "ចូលគណនី (Sign In)",
    workEmail: "Work email",
    phoneNumber: "លេខទូរស័ព្ទ",
    confirmPassword: "បញ្ជាក់ពាក្យសម្ងាត់",
    match: "✓ ត្រឹមត្រូវ",
    mismatch: "✗ មិនដូចគ្នា",
    back: "ត្រឡប់",
    companyName: "ឈ្មោះក្រុមហ៊ុន ឬអង្គភាព",
    businessType: "ប្រភេទអាជីវកម្ម",
    licenceNumber: "លេខចុះបញ្ជី ឬអាជ្ញាប័ណ្ណ",
    uploadLicence: "Upload ឯកសារចុះបញ្ជី (JPG, PNG, PDF)",
    fileFormats: "JPG, PNG ឬ PDF (Max 10MB)",
    agreeAdRules: "យល់ព្រមលើ គោលការណ៍ផ្សព្វផ្សាយ",
    completeReg: "បញ្ចប់ការចុះឈ្មោះ ✓",
    proceedDashboard: "ចូលទៅកាន់ផ្ទាំងគ្រប់គ្រង →",
    validEmailError: "សូមបញ្ចូល Work Email ដែលត្រឹមត្រូវ",
    phoneError: "សូមបញ្ចូលលេខទូរស័ព្ទរបស់អ្នក",
    pwdLenError: "Password must be at least 15 characters",
    pwdMatchError: "ពាក្យសម្ងាត់ទាំងពីរមិនដូចគ្នាទេ",
    termsError: "សូមយល់ព្រមលើ Terms of Service & Privacy Policy",
    otpLenError: "Enter all six digits from your email",
    companyError: "សូមបញ្ចូលឈ្មោះក្រុមហ៊ុន ឬអង្គភាព",
    licenceError: "សូមបញ្ចូលលេខចុះបញ្ជីពាណិជ្ជកម្ម ឬអាជ្ញាប័ណ្ណ",
    uploadError: "សូម Upload ឯកសារអាជ្ញាប័ណ្ណចុះបញ្ជី",
    adRulesError: "សូមយល់ព្រមលើគោលការណ៍ផ្សព្វផ្សាយ",
    fillAllError: "សូមបំពេញព័ត៌មានទាំងអស់",
    phoneFirst: "Enter your email and password first",
    codeSent: "Verification code sent to your email",
    fileTypeError: "អនុញ្ញាតតែឯកសារ JPG, PNG ឬ PDF ប៉ុណ្ណោះ",
    fileSizeError: "ទំហំឯកសារមិនត្រូវលើសពី 10MB",
    choosePlanTitle: "ជ្រើសរើសកញ្ចប់គម្រោងអាជីវកម្ម",
    choosePlanSubtitle: "ជ្រើសរើសកញ្ចប់ដែលស័ក្តិសមនឹងទំហំអាជីវកម្មរបស់អ្នក។ អាចផ្លាស់ប្តូរបានគ្រប់ពេល។",
    planStandard: "កញ្ចប់ស្តង់ដារ",
    planAdvanced: "កញ្ចប់កម្រិតខ្ពស់",
    planFlagship: "កញ្ចប់ Flagship",
    planVip: "កញ្ចប់ VIP ផ្តាច់មុខ",
    planSubStandard: "★ មុខងារមូលដ្ឋានអាជីវកម្ម",
    planSubAdvanced: "★ រួមបញ្ចូលមុខងារស្តង់ដារទាំងអស់",
    planSubFlagship: "★ រួមបញ្ចូលមុខងារកម្រិតខ្ពស់ទាំងអស់",
    planSubVip: "★ មុខងារពិសេសគ្រប់យ៉ាងឥតកំណត់",
    recommendedRibbon: "👍 ពេញនិយម 70%",
    selectPlan: "ជ្រើសរើសគម្រោង",
    skipToDashboard: "រំលងទៅកាន់ផ្ទាំងគ្រប់គ្រង →",
    originalPrice: "តម្លៃដើម",
    customEnterpriseContact: "ត្រូវការដំណោះស្រាយពិសេសសម្រាប់សាជីវកម្មធំ ឬតភ្ជាប់ ERP? ទាក់ទងផ្នែកលក់",
  },
  zh: {
    finishSignupTitle: "完成注册",
    signingUpWith: "注册账号：",
    finishGoogleHint: "请添加电话号码并接受条款。",
    finishTelegramHint: "请添加工作邮箱和电话号码，我们会通过邮件发送验证码。",
    loginTitle: "登录 LoyaltyBridge",
    orDivider: "或",
    continueWithTelegram: "使用 Telegram 账号继续",
    emailPlaceholder: "电子邮箱地址",
    continueBtn: "继续",
    checkInboxTitle: "请查收邮件",
    checkInboxBefore: "请输入我们刚刚发送至 ",
    checkInboxAfter: " 的验证码",
    codeLabel: "验证码",
    resendEmail: "重新发送邮件",
    continueWithPassword: "使用密码继续",
    enterPasswordTitle: "请输入密码",
    codeLenError: "请输入完整的6位验证码",
    socialUnavailable: "此登录方式暂不可用",
    socialFailed: "登录失败，请重试。",
    googleNotLinked: "没有使用此 Google 邮箱的有效账户，请先注册。",
    telegramNotLinked: "此 Telegram 尚未关联账户。请先用其他方式登录，然后在账户页面关联。",
    brandSubtitle: "企业忠诚度与奖励平台",
    home: "首页",
    signUpTitle: "创建企业账户",
    password: "密码",
    passwordPlaceholder: "请输入密码",
    passwordMinHint: "至少15位字符",
    forgotPassword: "忘记密码？",
    verificationCode: "验证码",
    codePlaceholder: "6位数验证码",
    getCode: "获取验证码",
    agreeTo: "我已阅读并同意 ",
    and: " 与 ",
    terms: "用户服务协议",
    privacy: "隐私政策",
    continueWithGoogle: "使用 Google 账号继续",
    dontHaveAccount: "还没有账户？",
    signUpLink: "立即注册",
    alreadyHaveAccount: "已有账户？",
    signInLink: "立即登录",
    workEmail: "企业工作邮箱",
    phoneNumber: "手机号码",
    confirmPassword: "确认密码",
    match: "✓ 密码一致",
    mismatch: "✗ 密码不匹配",
    back: "返回",
    companyName: "企业或机构名称",
    businessType: "企业业务类型",
    licenceNumber: "商业登记/营业执照号",
    uploadLicence: "上传营业执照文件 (JPG, PNG, PDF)",
    fileFormats: "支持 JPG、PNG 或 PDF（最大 10MB）",
    agreeAdRules: "我同意广告推广合规守则",
    completeReg: "完成注册 ✓",
    proceedDashboard: "进入控制面板 →",
    validEmailError: "请输入有效的工作邮箱",
    phoneError: "请输入您的手机号码",
    pwdLenError: "Password must be at least 15 characters",
    pwdMatchError: "两次输入的密码不一致",
    termsError: "请阅读并同意服务条款与隐私政策",
    otpLenError: "Enter all six digits from your email",
    companyError: "请输入企业或机构名称",
    licenceError: "请输入营业执照编号",
    uploadError: "请上传营业执照文件",
    adRulesError: "请同意广告合规守则",
    fillAllError: "请填写所有必填信息",
    phoneFirst: "Enter your email and password first",
    codeSent: "Verification code sent to your email",
    fileTypeError: "仅支持 JPG、PNG 或 PDF 格式文件",
    fileSizeError: "文件大小不能超过 10MB",
    choosePlanTitle: "选择您的企业服务方案",
    choosePlanSubtitle: "根据您的团队与业务规模选择最适合的版本，支持随时升级。",
    planStandard: "标准版",
    planAdvanced: "高级版",
    planFlagship: "旗舰版",
    planVip: "尊享版",
    planSubStandard: "★ 满足直播基本需求",
    planSubAdvanced: "★ 包含标准版全部功能",
    planSubFlagship: "★ 包含高级版全部功能",
    planSubVip: "★ 包含导购版全部功能",
    recommendedRibbon: "👍 70% 客户推荐",
    selectPlan: "立即购买",
    skipToDashboard: "暂不购买，去逛逛 →",
    originalPrice: "原价",
    customEnterpriseContact: "需要大企业专属方案、私有化部署或深度定制？联系商务顾问",
  }
};

function getPasswordStrength(pwd: string): { score: number; color: string } {
  if (!pwd) return { score: 0, color: "bg-neutral-200" };
  let score = 0;
  if (pwd.length >= MINIMUM_PASSWORD_LENGTH) score++;
  if (pwd.length >= 20) score++;
  if (pwd.length >= 25) score++;
  if (score <= 1) return { score: 1, color: "bg-rose-500" };
  if (score === 2) return { score: 2, color: "bg-amber-500" };
  return { score: 3, color: "bg-emerald-500" };
}

function LoginDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-4 py-2 text-xs text-neutral-700" role="separator">
      <div className="h-px flex-1 bg-neutral-500/40" />
      <span>{label}</span>
      <div className="h-px flex-1 bg-neutral-500/40" />
    </div>
  );
}

export default function AuthPage({ initialMode = "login" }: AuthPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get("step");
  const planParam = searchParams.get("plan");
  const modeParam = searchParams.get("mode");
  const [isSignUp, setIsSignUp] = useState(initialMode === "signup" || modeParam === "signup" || stepParam === "4");
  const [signUpStep, setSignUpStep] = useState<1 | 2 | 3 | 4>(
    stepParam === "4" ? 4 : 1
  );
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(planParam || null);

  const { lang } = useLanguage();

  // Country code dropdown state
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  // Sign-up states
  const [workEmail, setWorkEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [policies, setPolicies] = useState<PolicyDocument[]>([]);
  const [reviewPolicy, setReviewPolicy] = useState<PolicyDocument['document_type'] | null>(null);
  const [policyError, setPolicyError] = useState('');
  const [policyAttempt, setPolicyAttempt] = useState(0);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  useEffect(() => {
    let active = true;
    if (isSignUp) {
      setPoliciesLoading(true);
      setPolicyError('');
      readPolicies().then((items) => {
        if (active) { setPolicies(items); setPolicyError(''); }
      }).catch((failure) => {
        if (active) { setPolicies([]); setPolicyError(failure.message); }
      }).finally(() => { if (active) setPoliciesLoading(false); });
    }
    return () => { active = false; };
  }, [isSignUp, policyAttempt]);

  // Phone verification state
  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Company details state
  const [companyName, setCompanyName] = useState("");
  const [businessType, setBusinessType] = useState("retail");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: string; type: string } | null>(null);
  const [agreeAdRules, setAgreeAdRules] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Login form state
  const [loginStep, setLoginStep] = useState<"email" | "code" | "password">("email");
  const [loginChallengeId, setLoginChallengeId] = useState<string | null>(null);
  const [loginCode, setLoginCode] = useState("");
  const [providers, setProviders] = useState<SocialProviders | null>(null);
  const [socialOffer, setSocialOffer] = useState<SocialSignupOffer | null>(null);
  const telegramLogin = useRef<ReturnType<typeof createTelegramLogin> | null>(null);
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginShowPassword, setLoginShowPassword] = useState(false);

  // General state
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  function clearField(field: string) {
    setFieldErrors((current) => ({ ...current, [field]: '' }));
    setErrorMsg(null);
  }
  function reportFields(errors: Record<string, string>) {
    setFieldErrors(errors);
    const first = Object.keys(errors)[0];
    if (first) requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-auth-field="${first}"]`)?.focus());
    return Boolean(first);
  }
  function fieldAttributes(field: string) {
    return { 'data-auth-field': field, 'aria-invalid': Boolean(fieldErrors[field]),
      'aria-describedby': fieldErrors[field] ? `auth-${field}-error` : undefined };
  }
  const [loading, setLoading] = useState(false);
  // A ref blocks repeated clicks synchronously, before React renders the disabled button.
  const submitLock = useRef(false);
  function beginRequest() {
    if (submitLock.current) return false;
    submitLock.current = true;
    setLoading(true);
    return true;
  }
  function endRequest() { submitLock.current = false; setLoading(false); }

  const [signupChallengeId, setSignupChallengeId] = useState<string | null>(null);

  function rememberSignupChallenge(challenge: { challengeId: string; resendAfterSeconds: number }) {
    setSignupChallengeId(challenge.challengeId);
    setCountdown(challenge.resendAfterSeconds);
    setCanResend(false);
    setOtp("");
  }

  function handleDeliveryFailure(error: unknown, email = workEmail) {
    if (error instanceof AuthApiError && error.code === 'EMAIL_UNAVAILABLE' && error.challenge) {
      // Save the replacement ID even when SMTP fails, so Resend can recover later.
      rememberSignupChallenge(error.challenge);
      setWorkEmail(email);
      setIsSignUp(true);
      setSignUpStep(2);
    }
    showFailure(error);
  }

  const showFailure = (error: unknown) => {
    if (error instanceof AuthApiError && error.code === 'POLICY_CHANGED') {
      setAgreeTerms(false);
      setPolicyAttempt((attempt) => attempt + 1);
    }
    const message = error instanceof Error ? error.message : 'Request failed. Please try again.';
    const field = error instanceof AuthApiError ? ({ INVALID_CREDENTIALS: 'loginPassword', INVALID_CODE: 'otp',
      CODE_RATE_LIMITED: signUpStep === 2 ? 'otp' : '', POLICY_CHANGED: 'terms', POLICY_NOT_FOUND: 'terms', POLICIES_UNAVAILABLE: 'terms' } as Record<string, string>)[error.code] : undefined;
    if (field) reportFields({ [field]: message });
    else setErrorMsg(message);
  };




  const t = I18N[lang] || I18N.en;

  useEffect(() => { setFieldErrors({}); }, [isSignUp, signUpStep]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // OTP Countdown Timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Step 1 validation & proceed to Step 2
  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitLock.current) return;
    setErrorMsg(null);

    const errors: Record<string, string> = {};
    const phone = normalizePhone(selectedCountry.code, phoneNumber);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workEmail.trim()) || workEmail.trim().length > 254) errors.email = t.validEmailError;
    if (!/^\+[1-9][0-9]{7,14}$/.test(phone)) errors.phone = t.phoneError;
    if (password.length < MINIMUM_PASSWORD_LENGTH) errors.password = t.pwdLenError;
    else if (password.length > 128) errors.password = 'Password must be at most 128 characters.';
    if (!confirmPassword || password !== confirmPassword) errors.confirmPassword = t.pwdMatchError;
    if (!agreeTerms) errors.terms = t.termsError;
    if (policyError || policies.length !== 2) errors.terms = policyError || 'Policies are loading. Please try again.';
    if (reportFields(errors)) return;
    if (!beginRequest()) return;
    try {
      const terms = policies.find((item) => item.document_type === 'terms_of_service');
      const privacy = policies.find((item) => item.document_type === 'privacy_policy');
      if (!terms || !privacy) throw new Error(policyError || 'Policies are loading. Please try again.');
      const challenge = await signup({ email: workEmail.trim(), phone, password,
        acceptTerms: agreeTerms, termsVersion: terms.version, privacyVersion: privacy.version,
        termsDocumentId: terms.id, privacyDocumentId: privacy.id });
      setSignupChallengeId(challenge.challengeId);
      setCountdown(challenge.resendAfterSeconds);
      setCanResend(false);
      setOtp("");
      setSignUpStep(2);
    } catch (error) { handleDeliveryFailure(error); }
    finally { endRequest(); }
  };

  // Step 2 OTP logic
  const handleResendOtp = async () => {
    if (!canResend || !signupChallengeId || submitLock.current) return;
    if (!beginRequest()) return;
    setErrorMsg(null);
    try {
      const challenge = await resendCode(signupChallengeId);
      setSignupChallengeId(challenge.challengeId);
      setCountdown(challenge.resendAfterSeconds);
      setCanResend(false);
      setOtp("");
    } catch (error) { handleDeliveryFailure(error); }
    finally { endRequest(); }
  };

  const handleStep2Verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitLock.current) return;
    setErrorMsg(null);
    if (!signupChallengeId || !/^\d{6}$/.test(otp)) {
      reportFields({ otp: t.otpLenError });
      return;
    }
    setFieldErrors({});
    if (!beginRequest()) return;
    try {
      await verifyEmail(signupChallengeId, otp);
      setPassword('');
      setConfirmPassword('');
      navigate('/account');
    } catch (error) { showFailure(error); }
    finally { endRequest(); }
  };

  // Handle File Upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setErrorMsg(t.fileTypeError);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setErrorMsg(t.fileSizeError);
        return;
      }
      setUploadedFile({
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        type: file.type.includes("pdf") ? "PDF" : "Image",
      });
      setErrorMsg(null);
    }
  };

  // Handle Step 3 Submit
  const handleStep3Submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!companyName.trim()) {
      setErrorMsg(t.companyError);
      return;
    }
    if (!registrationNumber.trim()) {
      setErrorMsg(t.licenceError);
      return;
    }
    if (!uploadedFile) {
      setErrorMsg(t.uploadError);
      return;
    }
    if (!agreeAdRules) {
      setErrorMsg(t.adRulesError);
      return;
    }

    setErrorMsg('Company document submission is not connected yet.');
  };

  // Load provider scripts ahead of time: popups only open synchronously from the click.
  useEffect(() => {
    let active = true;
    socialProviders().then((available) => {
      if (!active) return;
      setProviders(available);
      if (available.google) void prepareGoogle().catch(() => undefined);
      if (available.telegram) {
        telegramLogin.current = createTelegramLogin(available.telegram.clientId);
        void telegramLogin.current.prepare().catch(() => undefined);
      }
    }).catch(() => undefined);
    return () => { active = false; };
  }, []);

  function openAccount(account: AccountIdentity) {
    setLoginPassword('');
    navigate(account.dashboardPages?.[0] ?? '/account');
  }

  function showSocialFailure(notLinked: string, error: unknown) {
    const code = error instanceof AuthApiError ? error.code : '';
    setErrorMsg(code === 'ACCOUNT_NOT_LINKED' ? notLinked
      : code === 'PROVIDER_NOT_CONFIGURED' ? t.socialUnavailable : t.socialFailed);
  }

  // A new Google or Telegram identity continues on the short finish form.
  function openAccountOrFinishSignup(result: AccountIdentity | SocialSignupOffer) {
    if (!('signupRequired' in result)) { openAccount(result); return; }
    setSocialOffer(result);
    setWorkEmail(result.email ?? '');
    const country = result.phone ? COUNTRY_CODES.find((c) => result.phone?.startsWith(c.code)) : undefined;
    if (country && result.phone) { setSelectedCountry(country); setPhoneNumber(result.phone.slice(country.code.length)); }
    setErrorMsg(null);
    setFieldErrors({});
    setSignUpStep(1);
    setIsSignUp(true);
  }

  const handleSocialFinish = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitLock.current || !socialOffer) return;
    setErrorMsg(null);
    const errors: Record<string, string> = {};
    const phone = normalizePhone(selectedCountry.code, phoneNumber);
    const needsEmail = socialOffer.provider === 'telegram';
    if (needsEmail && (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workEmail.trim()) || workEmail.trim().length > 254)) errors.email = t.validEmailError;
    if (!/^\+[1-9][0-9]{7,14}$/.test(phone)) errors.phone = t.phoneError;
    if (!agreeTerms) errors.terms = t.termsError;
    const terms = policies.find((item) => item.document_type === 'terms_of_service');
    const privacy = policies.find((item) => item.document_type === 'privacy_policy');
    if (!terms || !privacy) errors.terms = policyError || 'Policies are loading. Please try again.';
    if (reportFields(errors) || !terms || !privacy) return;
    if (!beginRequest()) return;
    try {
      const result = await socialSignup({ signupToken: socialOffer.signupToken, phone, acceptTerms: agreeTerms,
        termsVersion: terms.version, privacyVersion: privacy.version, termsDocumentId: terms.id, privacyDocumentId: privacy.id,
        ...(needsEmail ? { email: workEmail.trim() } : {}) });
      if ('challengeId' in result) {
        rememberSignupChallenge(result);
        setSocialOffer(null);
        setSignUpStep(2);
      } else {
        openAccount(result);
      }
    } catch (error) {
      if (error instanceof AuthApiError && error.code === 'SIGNUP_EXPIRED') setSocialOffer(null);
      handleDeliveryFailure(error);
    }
    finally { endRequest(); }
  };

  const handleGoogleContinue = async () => {
    setErrorMsg(null);
    if (!providers?.google) { setErrorMsg(t.socialUnavailable); return; }
    if (submitLock.current) return;
    try {
      const code = await requestGoogleCode(providers.google.clientId);
      if (!code || !beginRequest()) return;
      try { openAccountOrFinishSignup(await googleSignin(code)); }
      finally { endRequest(); }
    } catch (error) { showSocialFailure(t.googleNotLinked, error); }
  };

  const handleTelegramContinue = async () => {
    setErrorMsg(null);
    const login = telegramLogin.current;
    if (!login) { setErrorMsg(t.socialUnavailable); return; }
    if (submitLock.current) return;
    try {
      const idToken = await login.open(isSignUp);
      if (!idToken || !beginRequest()) return;
      try { openAccountOrFinishSignup(await telegramSignin(idToken)); }
      finally { endRequest(); }
    } catch (error) { showSocialFailure(t.telegramNotLinked, error); }
    // Each nonce works once, so get a fresh one for the next attempt.
    finally { void login.prepare().catch(() => undefined); }
  };

  const goToLoginStep = (step: "email" | "code" | "password") => {
    setErrorMsg(null);
    setFieldErrors({});
    setLoginStep(step);
  };

  const sendLoginCode = async () => {
    if (!beginRequest()) return false;
    setErrorMsg(null);
    try {
      const challenge = await requestSigninCode(loginIdentifier.trim());
      setLoginChallengeId(challenge.challengeId);
      setLoginCode("");
      setCountdown(challenge.resendAfterSeconds);
      setCanResend(false);
      return true;
    } catch (error) { showFailure(error); return false; }
    finally { endRequest(); }
  };

  const handleLoginEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitLock.current) return;
    setErrorMsg(null);
    const email = loginIdentifier.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254) {
      reportFields({ loginEmail: t.validEmailError });
      return;
    }
    setFieldErrors({});
    if (await sendLoginCode()) setLoginStep("code");
  };

  const handleLoginCodeSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitLock.current) return;
    setErrorMsg(null);
    if (!loginChallengeId || !/^\d{6}$/.test(loginCode)) {
      reportFields({ otp: t.codeLenError });
      return;
    }
    setFieldErrors({});
    if (!beginRequest()) return;
    try { openAccount(await verifyEmail(loginChallengeId, loginCode)); }
    catch (error) { showFailure(error); }
    finally { endRequest(); }
  };

  // Every step uses the plain white layout; only plan selection needs its wide card area.
  const plainLayout = !(isSignUp && signUpStep === 4);
  const signInSwitch = (
    <p className="text-center pt-3 text-sm text-neutral-700">
      {t.alreadyHaveAccount}
      <button
        type="button"
        onClick={() => { setIsSignUp(false); setSocialOffer(null); setErrorMsg(null); }}
        className="text-neutral-900 font-semibold hover:underline ml-1.5 cursor-pointer"
      >
        {t.signInLink}
      </button>
    </p>
  );
  const continueLabel = (busy: boolean) => (busy ? <i className="ri-loader-4-line animate-spin text-lg" role="status" aria-label="Loading" /> : t.continueBtn);

  const phoneField = (
                    <div>
                      <div className="flex gap-2">
                        <div className="relative shrink-0" ref={countryDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                            aria-haspopup="listbox"
                            aria-expanded={countryDropdownOpen}
                            className="h-[52px] rounded-full bg-white border border-neutral-300 hover:bg-neutral-50 px-4 flex items-center gap-1.5 text-[15px] text-neutral-900 transition-colors cursor-pointer"
                          >
                            <CountryFlag iso={selectedCountry.iso} className="w-5 h-3.5 shrink-0 rounded-[2px]" />
                            <span>{selectedCountry.code}</span>
                            <i className={`ri-arrow-down-s-line text-base text-neutral-400 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {countryDropdownOpen && (
                            <div className="absolute left-0 mt-2 w-56 bg-white border border-neutral-200 shadow-xl z-50 rounded-xl animate-fade-in p-1.5 max-h-60 overflow-y-auto">
                              {COUNTRY_CODES.map((c) => (
                                <button
                                  key={c.code}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCountry(c);
                                    setCountryDropdownOpen(false);
                                  }}
                                  className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm text-left hover:bg-neutral-50 transition-colors cursor-pointer ${selectedCountry.code === c.code ? "bg-primary-50 text-primary-700 font-medium" : "text-neutral-800"}`}
                                >
                                  <span className="flex items-center gap-2">
                                    <CountryFlag iso={c.iso} className="w-5 h-3.5 shrink-0 rounded-[2px]" />
                                    <span>{lang === "km" ? c.nameKm : lang === "zh" ? c.nameZh : c.name}</span>
                                  </span>
                                  <span className="text-neutral-400 text-xs">{c.code}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <input
                          type="tel"
                          value={phoneNumber}
                          {...fieldAttributes('phone')}
                          aria-label={t.phoneNumber}
                          onChange={(e) => { setPhoneNumber(e.target.value); clearField('phone'); }}
                          placeholder={t.phoneNumber}
                          className={`${AUTH_INPUT} min-w-0 flex-1`}
                          autoComplete="tel"
                        />
                      </div>
                      <FieldMessage field="phone" message={fieldErrors.phone} />
                    </div>
  );
  const termsField = (
    <>
                    <div className="flex items-start gap-2.5 pt-1 pl-1 select-none text-sm text-neutral-800">
                      <button
                        type="button"
                        onClick={() => { setAgreeTerms(!agreeTerms); clearField('terms'); }}
                        {...fieldAttributes('terms')}
                        aria-label={t.terms + ' & ' + t.privacy}
                        className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 transition-all border cursor-pointer ${agreeTerms
                            ? "bg-neutral-900 border-neutral-900 text-white"
                            : "bg-white border-neutral-300 hover:border-neutral-400 text-transparent"
                          }`}
                        role="checkbox"
                        aria-checked={agreeTerms}
                      >
                        <i className="ri-check-line text-[10px] font-black" />
                      </button>
                      <p className="leading-snug">
                        {t.agreeTo}
                        <button type="button" onClick={() => setReviewPolicy('terms_of_service')} aria-haspopup="dialog" className="text-neutral-900 underline underline-offset-2 hover:text-neutral-600 cursor-pointer">
                          {t.terms}
                        </button>
                        {t.and}
                        <button type="button" onClick={() => setReviewPolicy('privacy_policy')} aria-haspopup="dialog" className="text-neutral-900 underline underline-offset-2 hover:text-neutral-600 cursor-pointer">
                          {t.privacy}
                        </button>
                      </p>
                    </div>

                    <FieldMessage field="terms" message={fieldErrors.terms || policyError} />
                    {policyError && <button type="button" disabled={loading || policiesLoading}
                      onClick={() => { setAgreeTerms(false); clearField('terms'); setPolicyAttempt((attempt) => attempt + 1); }}
                      className="inline-flex min-h-11 cursor-pointer items-center text-xs text-primary-700 underline disabled:cursor-wait disabled:opacity-60">Retry loading policies</button>}
    </>
  );

  // Offer only sign-in methods the server has keys for. If that check fails, keep them visible.
  const showGoogle = !providers || Boolean(providers.google);
  const showTelegram = !providers || Boolean(providers.telegram);

  const loginTitles = { email: t.loginTitle, code: t.checkInboxTitle, password: t.enterPasswordTitle };

  const handleLoginSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitLock.current) return;
    setErrorMsg(null);
    const errors: Record<string, string> = {};
    if (!loginPassword) errors.loginPassword = t.passwordPlaceholder;
    else if (loginPassword.length > 128) errors.loginPassword = 'Password must be at most 128 characters.';
    if (reportFields(errors)) return;
    if (!beginRequest()) return;
    try {
      const result = await signin(loginIdentifier.trim(), loginPassword);
      if ('challengeId' in result) {
        // Unverified accounts complete the existing signup email-verification screen.
        setWorkEmail(loginIdentifier.trim());
        setSignupChallengeId(result.challengeId);
        setCountdown(result.resendAfterSeconds);
        setCanResend(false);
        setOtp("");
        setIsSignUp(true);
        setSignUpStep(2);
      } else {
        openAccount(result);
      }
    } catch (error) { handleDeliveryFailure(error, loginIdentifier.trim()); }
    finally { endRequest(); }
  };

  async function handleDemoLogin(role: string) {
    if (!beginRequest()) return;
    setErrorMsg(null);
    setFieldErrors({});
    try {
      const account = await demoSignin(role);
      navigate(account.dashboardPages?.[0] ?? '/account');
    } catch (error) { showFailure(error); }
    finally { endRequest(); }
  }

  const handleSelectPlan = (planId: string) => {
    setSelectedPlanId(planId);
    if (!beginRequest()) return;
    setTimeout(() => {
      endRequest();
      navigate(`/dashboard?plan=${planId}`);
    }, 450);
  };
  const pwdStrength = getPasswordStrength(password);

  return (
    <div
      lang={lang}
      className={`min-h-screen ${plainLayout ? "auth-background" : "bg-[#f8fafc]"} relative flex flex-col justify-between ${
        lang === "km" ? "font-khmer" : "font-sans"
      } text-neutral-900 selection:bg-primary-100 antialiased overflow-x-hidden`}
    >

      {/* CLEAN SUBTLE DOT PATTERN */}
      {!plainLayout && (
        <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:24px_24px] opacity-40 pointer-events-none" />
      )}

      {/* TOP GLASS NAVBAR */}
      <AuthHeader subtitle={t.brandSubtitle} homeLabel={t.home} plain={plainLayout} />

      {/* MAIN CONTENT AREA: CENTERED COMPACT FORM CARD WITH PRIMARY BLUE BLUR INSIDE */}
      <main className="flex-1 flex items-center justify-center p-3 sm:p-5 relative z-10">
        <div className={`w-full transition-all duration-300 ${
          plainLayout ? "max-w-[380px]" : "max-w-7xl"
        }`}>

          {/* THE CARD CONTAINER (CLEAN & MINIMALIST) */}
          <div className={`relative transition-all ${plainLayout ? "" : "overflow-hidden p-0 sm:p-2 bg-transparent border-0 shadow-none"}`}>

            {plainLayout && (
              <div className="text-center mb-6">
                <h1 className={AUTH_TITLE}>
                  {!isSignUp ? loginTitles[loginStep] : socialOffer ? t.finishSignupTitle : signUpStep === 2 ? t.checkInboxTitle : t.signUpTitle}
                </h1>
                {isSignUp && (
                  <div className="flex items-center justify-center gap-1.5 mt-3" aria-hidden="true">
                    {[1, 2, 3].map((step) => (
                      <div key={step} className={`h-1 w-6 rounded-full transition-all ${signUpStep >= step ? "bg-neutral-900" : "bg-neutral-300"}`} />
                    ))}
                  </div>
                )}
                {((!isSignUp && loginStep === "code") || (isSignUp && signUpStep === 2)) && (
                  <p className="mt-3 text-[15px] leading-6 text-neutral-800 break-words">
                    {t.checkInboxBefore}
                    <span className="text-neutral-900">{isSignUp ? workEmail : loginIdentifier.trim()}</span>
                    {t.checkInboxAfter}
                  </p>
                )}
                {!isSignUp && loginStep === "password" && (
                  <p className="mt-3 text-[15px] leading-6 text-neutral-800 break-words">{loginIdentifier.trim()}</p>
                )}
              </div>
            )}

            {/* ======================================================= */}
            {/* MODE 1: SIGN IN FORM                                    */}
            {/* ======================================================= */}
            {!isSignUp ? (
              <div className="space-y-3" aria-busy={loading}>
                <FieldMessage field="form" message={errorMsg} />

                {/* STEP 1: PROVIDERS & EMAIL */}
                {loginStep === "email" && (
                  <>
                    {showGoogle && <GoogleContinueButton label={t.continueWithGoogle} disabled={loading} className={AUTH_OUTLINE_BTN} onClick={handleGoogleContinue} />}
                    {showTelegram && <button type="button" disabled={loading} onClick={handleTelegramContinue} className={AUTH_OUTLINE_BTN}>
                      <i className="ri-telegram-fill text-xl leading-none text-[#2aabee] shrink-0" aria-hidden="true" />
                      <span>{t.continueWithTelegram}</span>
                    </button>}

                    {(showGoogle || showTelegram) && <LoginDivider label={t.orDivider} />}

                    <form noValidate onSubmit={handleLoginEmailSubmit} className="space-y-4">
                      <div>
                        <input
                          type="email"
                          value={loginIdentifier}
                          {...fieldAttributes('loginEmail')}
                          aria-label={t.emailPlaceholder}
                          onChange={(e) => { setLoginIdentifier(e.target.value); clearField('loginEmail'); }}
                          placeholder={t.emailPlaceholder}
                          autoComplete="username"
                          className={AUTH_INPUT}
                        />
                        <FieldMessage field="loginEmail" message={fieldErrors.loginEmail} />
                      </div>
                      <button type="submit" disabled={loading} className={AUTH_PRIMARY_BTN}>
                        {loading ? <i className="ri-loader-4-line animate-spin text-lg" role="status" aria-label="Loading" /> : t.continueBtn}
                      </button>
                    </form>

                    <p className="text-center pt-3 text-sm text-neutral-700">
                      {t.dontHaveAccount}
                      <button
                        type="button"
                        onClick={() => {
                          setIsSignUp(true);
                          setSignUpStep(1);
                          setErrorMsg(null);
                        }}
                        className="text-neutral-900 font-semibold hover:underline ml-1.5 cursor-pointer"
                      >
                        {t.signUpLink}
                      </button>
                    </p>
                    {import.meta.env.DEV && <DemoAccounts busy={loading} onSelect={handleDemoLogin} />}
                  </>
                )}

                {/* STEP 2: EMAILED CODE */}
                {loginStep === "code" && (
                  <>
                    <form noValidate onSubmit={handleLoginCodeSubmit} className="space-y-4">
                      <div>
                        <div className="relative">
                          <label htmlFor="login-code" className="absolute -top-2 left-4 rounded bg-white px-1 text-xs leading-4 text-primary-600">
                            {t.codeLabel}
                          </label>
                          <input
                            id="login-code"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoFocus
                            maxLength={6}
                            value={loginCode}
                            {...fieldAttributes('otp')}
                            onChange={(e) => { setLoginCode(e.target.value.replace(/\D/g, "")); clearField('otp'); }}
                            className={AUTH_INPUT}
                          />
                        </div>
                        <FieldMessage field="otp" message={fieldErrors.otp} />
                      </div>
                      <button type="submit" disabled={loading} className={AUTH_PRIMARY_BTN}>
                        {loading ? <i className="ri-loader-4-line animate-spin text-lg" role="status" aria-label="Loading" /> : t.continueBtn}
                      </button>
                    </form>

                    <button
                      type="button"
                      disabled={!canResend || loading}
                      onClick={() => { void sendLoginCode(); }}
                      className="w-full h-10 text-[15px] text-neutral-800 hover:text-neutral-950 disabled:text-neutral-500 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                      {t.resendEmail}{canResend ? "" : ` (${countdown}s)`}
                    </button>

                    <LoginDivider label={t.orDivider} />

                    <button type="button" onClick={() => goToLoginStep("password")} className={AUTH_OUTLINE_BTN}>
                      {t.continueWithPassword}
                    </button>
                    <div className="flex justify-center pt-1">
                      <button type="button" onClick={() => goToLoginStep("email")} aria-label={t.back} title={t.back} className={AUTH_ICON_BTN}>
                        <i className="ri-arrow-left-line text-xl leading-none" aria-hidden="true" />
                      </button>
                    </div>
                  </>
                )}

                {/* STEP 2 (ALT): PASSWORD */}
                {loginStep === "password" && (
                  <>
                    <form noValidate onSubmit={handleLoginSubmit} className="space-y-4">
                      <input type="email" value={loginIdentifier} readOnly hidden autoComplete="username" />
                      <div>
                        <div className="relative">
                          <input
                            type={loginShowPassword ? "text" : "password"}
                            value={loginPassword}
                            {...fieldAttributes('loginPassword')}
                            aria-label={t.password}
                            onChange={(e) => { setLoginPassword(e.target.value); clearField('loginPassword'); }}
                            placeholder={t.passwordPlaceholder}
                            autoComplete="current-password"
                            autoFocus
                            className={`${AUTH_INPUT} pr-12`}
                          />
                          <button
                            type="button"
                            onClick={() => setLoginShowPassword(!loginShowPassword)}
                            aria-pressed={loginShowPassword}
                            aria-label={t.password}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 cursor-pointer"
                          >
                            <i className={loginShowPassword ? "ri-eye-line text-lg" : "ri-eye-off-line text-lg"} />
                          </button>
                        </div>
                        <FieldMessage field="loginPassword" message={fieldErrors.loginPassword} />
                      </div>
                      <button type="submit" disabled={loading} className={AUTH_PRIMARY_BTN}>
                        {loading ? <i className="ri-loader-4-line animate-spin text-lg" role="status" aria-label="Loading" /> : t.continueBtn}
                      </button>
                    </form>

                    <button type="button" onClick={() => navigate('/forgot-password')} className={AUTH_TEXT_BTN}>
                      {t.forgotPassword}
                    </button>
                    <div className="flex justify-center pt-1">
                      <button type="button" onClick={() => goToLoginStep("email")} aria-label={t.back} title={t.back} className={AUTH_ICON_BTN}>
                        <i className="ri-arrow-left-line text-xl leading-none" aria-hidden="true" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (

              /* ======================================================= */
              /* MODE 2: MULTI-STEP SIGN UP                              */
              /* ======================================================= */
              <div className="relative z-10">

                {/* ----------------- STEP 1: CREDENTIALS ----------------- */}
                {signUpStep === 1 && socialOffer && (
                  <form noValidate aria-busy={loading} onSubmit={handleSocialFinish}>
                    <fieldset disabled={loading} className="contents space-y-3">
                    <p className="text-center text-[15px] leading-6 text-neutral-800 break-words pb-1">
                      {socialOffer.provider === 'google'
                        ? <>{t.signingUpWith} <span className="text-neutral-900">{socialOffer.email}</span>. {t.finishGoogleHint}</>
                        : t.finishTelegramHint}
                    </p>
                    {socialOffer.provider === 'telegram' && (
                      <div>
                        <input
                          type="email"
                          value={workEmail}
                          {...fieldAttributes('email')}
                          aria-label={t.workEmail}
                          onChange={(e) => { setWorkEmail(e.target.value); clearField('email'); }}
                          placeholder={t.workEmail}
                          className={AUTH_INPUT}
                          autoComplete="email"
                        />
                        <FieldMessage field="email" message={fieldErrors.email} />
                      </div>
                    )}
                    {phoneField}
                    {termsField}
                    <FieldMessage field="form" message={errorMsg} />
                    <button
                      type="submit"
                      disabled={loading || policiesLoading || policies.length !== 2}
                      className={AUTH_PRIMARY_BTN}
                    >
                      {policiesLoading && !loading ? 'Loading policies...' : continueLabel(loading)}
                    </button>
                    <div className="flex justify-center pt-1">
                      <button type="button" onClick={() => { setSocialOffer(null); setErrorMsg(null); }} aria-label={t.back} title={t.back} className={AUTH_ICON_BTN}>
                        <i className="ri-arrow-left-line text-xl leading-none" aria-hidden="true" />
                      </button>
                    </div>
                  </fieldset>
                  </form>
                )}

                {signUpStep === 1 && !socialOffer && (
                  <form noValidate aria-busy={loading} onSubmit={handleStep1Submit}>
                    <fieldset disabled={loading} className="contents space-y-3">

                    <FieldMessage field="form" message={errorMsg} />
                    {showGoogle && <GoogleContinueButton label={t.continueWithGoogle} disabled={loading} className={AUTH_OUTLINE_BTN} onClick={handleGoogleContinue} />}
                    {showTelegram && <button type="button" disabled={loading} onClick={handleTelegramContinue} className={AUTH_OUTLINE_BTN}>
                      <i className="ri-telegram-fill text-xl leading-none text-[#2aabee] shrink-0" aria-hidden="true" />
                      <span>{t.continueWithTelegram}</span>
                    </button>}
                    {(showGoogle || showTelegram) && <LoginDivider label={t.orDivider} />}

                    <div>
                      <input
                        type="email"
                        value={workEmail}
                        {...fieldAttributes('email')}
                        aria-label={t.workEmail}
                        onChange={(e) => { setWorkEmail(e.target.value); clearField('email'); }}
                        placeholder={t.workEmail}
                        className={AUTH_INPUT}
                        autoComplete="email"
                      />
                      <FieldMessage field="email" message={fieldErrors.email} />
                    </div>

                    {phoneField}

                    <div>
                      <AuthPasswordInput
                        value={password}
                        {...fieldAttributes('password')}
                        aria-label={t.password}
                        onChange={(e) => { setPassword(e.target.value); clearField('password'); }}
                        placeholder={`${t.password} (${t.passwordMinHint})`}
                        autoComplete="new-password"
                      />
                      {password && (
                        <div className="grid grid-cols-3 gap-1 mt-2 px-5" aria-hidden="true">
                          <div className={`h-0.5 rounded-full ${pwdStrength.score >= 1 ? pwdStrength.color : "bg-neutral-200"}`} />
                          <div className={`h-0.5 rounded-full ${pwdStrength.score >= 2 ? pwdStrength.color : "bg-neutral-200"}`} />
                          <div className={`h-0.5 rounded-full ${pwdStrength.score >= 3 ? pwdStrength.color : "bg-neutral-200"}`} />
                        </div>
                      )}
                      <FieldMessage field="password" message={fieldErrors.password} />
                    </div>

                    <div>
                      <AuthPasswordInput
                        value={confirmPassword}
                        {...fieldAttributes('confirmPassword')}
                        aria-label={t.confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); clearField('confirmPassword'); }}
                        placeholder={t.confirmPassword}
                        autoComplete="new-password"
                      />
                      {confirmPassword && (
                        <p className={`mt-1.5 pl-5 text-xs ${password === confirmPassword ? 'text-emerald-600' : 'text-rose-500'}`}>
                          {password === confirmPassword ? t.match : t.mismatch}
                        </p>
                      )}
                      <FieldMessage field="confirmPassword" message={fieldErrors.confirmPassword} />
                    </div>

                    {termsField}
                    <button
                      type="submit"
                      disabled={loading || policiesLoading || policies.length !== 2}
                      className={AUTH_PRIMARY_BTN}
                    >
                      {policiesLoading && !loading ? 'Loading policies...' : continueLabel(loading)}
                    </button>

                    {signInSwitch}
                  </fieldset>
                  </form>
                )}

                {/* ----------------- STEP 2: EMAIL VERIFICATION ----------------- */}
                {signUpStep === 2 && (
                  <form noValidate aria-busy={loading} onSubmit={handleStep2Verify}>
                    <fieldset disabled={loading} className="contents space-y-4">
                    <div>
                      <div className="relative">
                        <label htmlFor="signup-code" className="absolute -top-2 left-4 rounded bg-white px-1 text-xs leading-4 text-primary-600">
                          {t.codeLabel}
                        </label>
                        <input
                          id="signup-code"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          autoFocus
                          maxLength={6}
                          value={otp}
                          {...fieldAttributes('otp')}
                          onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '')); clearField('otp'); }}
                          className={AUTH_INPUT}
                        />
                      </div>
                      <FieldMessage field="otp" message={fieldErrors.otp} />
                    </div>
                    <FieldMessage field="form" message={errorMsg} />

                    <button type="submit" disabled={loading} className={AUTH_PRIMARY_BTN}>
                      {continueLabel(loading)}
                    </button>

                    <button
                      type="button"
                      disabled={!canResend || loading}
                      onClick={handleResendOtp}
                      className="w-full h-10 text-[15px] text-neutral-800 hover:text-neutral-950 disabled:text-neutral-500 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                      {t.resendEmail}{canResend ? "" : ` (${countdown}s)`}
                    </button>

                    <div className="flex justify-center pt-1">
                      <button type="button" onClick={() => setSignUpStep(1)} aria-label={t.back} title={t.back} className={AUTH_ICON_BTN}>
                        <i className="ri-arrow-left-line text-xl leading-none" aria-hidden="true" />
                      </button>
                    </div>

                    {signInSwitch}
                  </fieldset>
                  </form>
                )}

                {/* ----------------- STEP 3: COMPANY DETAILS ----------------- */}
                {signUpStep === 3 && (
                  <form onSubmit={handleStep3Submit} className="space-y-3">
                    <input
                      type="text"
                      value={companyName}
                      aria-label={t.companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder={t.companyName}
                      className={AUTH_INPUT}
                    />

                    <div className="relative">
                      <select
                        value={businessType}
                        aria-label={t.businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                        className={`${AUTH_INPUT} appearance-none pr-11 cursor-pointer`}
                      >
                        {BUSINESS_TYPES.map((bt) => (
                          <option key={bt.id} value={bt.id}>
                            {lang === "km" ? bt.labelKm : lang === "zh" ? bt.labelZh : bt.labelEn}
                          </option>
                        ))}
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-lg text-neutral-400 pointer-events-none" aria-hidden="true" />
                    </div>

                    <input
                      type="text"
                      value={registrationNumber}
                      aria-label={t.licenceNumber}
                      onChange={(e) => setRegistrationNumber(e.target.value)}
                      placeholder={t.licenceNumber}
                      className={AUTH_INPUT}
                    />

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      aria-label={t.uploadLicence}
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    {uploadedFile ? (
                      <div className="border border-neutral-300 px-5 py-3 rounded-3xl flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <i className={uploadedFile.type === "PDF" ? "ri-file-pdf-line text-rose-500 text-xl" : "ri-image-line text-neutral-500 text-xl"} />
                          <div className="min-w-0">
                            <p className="font-medium text-neutral-900 truncate text-sm">{uploadedFile.name}</p>
                            <p className="text-xs text-neutral-500">{uploadedFile.size} • {uploadedFile.type}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setUploadedFile(null)}
                          aria-label="Remove file"
                          className="text-neutral-400 hover:text-rose-600 shrink-0 cursor-pointer"
                        >
                          <i className="ri-close-line text-lg" aria-hidden="true" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border border-dashed border-neutral-300 hover:border-neutral-500 px-5 py-4 text-center cursor-pointer hover:bg-neutral-50 transition-colors rounded-3xl"
                      >
                        <i className="ri-upload-cloud-2-line text-2xl text-neutral-500" aria-hidden="true" />
                        <span className="block text-sm font-medium text-neutral-800 mt-0.5">{t.uploadLicence}</span>
                        <span className="block text-xs text-neutral-400 mt-0.5">{t.fileFormats}</span>
                      </button>
                    )}

                    <div className="flex items-start gap-2.5 pt-1 pl-1 select-none text-sm text-neutral-800">
                      <button
                        type="button"
                        onClick={() => setAgreeAdRules(!agreeAdRules)}
                        className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 transition-all border cursor-pointer ${agreeAdRules
                            ? "bg-neutral-900 border-neutral-900 text-white"
                            : "bg-white border-neutral-300 hover:border-neutral-400 text-transparent"
                          }`}
                        role="checkbox"
                        aria-checked={agreeAdRules}
                      >
                        <i className="ri-check-line text-[10px] font-black" />
                      </button>
                      <p className="leading-snug">
                        {policies.some((p) => p.is_development) && <span className="block">Development draft policies (English) — testing only.</span>}
                        <button type="button" onClick={(e) => { e.preventDefault(); alert("Advertising rules"); }} className="text-neutral-900 underline underline-offset-2 hover:text-neutral-600 cursor-pointer">
                          {t.agreeAdRules}
                        </button>
                      </p>
                    </div>

                    <FieldMessage field="form" message={errorMsg} />

                    <button type="submit" disabled={loading} className={AUTH_PRIMARY_BTN}>
                      {loading ? <i className="ri-loader-4-line animate-spin text-lg" role="status" aria-label="Loading" /> : t.completeReg}
                    </button>

                    <div className="flex justify-center pt-1">
                      <button type="button" onClick={() => setSignUpStep(2)} aria-label={t.back} title={t.back} className={AUTH_ICON_BTN}>
                        <i className="ri-arrow-left-line text-xl leading-none" aria-hidden="true" />
                      </button>
                    </div>

                    {signInSwitch}
                  </form>
                )}

                {/* ----------------- STEP 4: CHOOSE PLAN (NEW DEDICATED COMPONENT) ----------------- */}
                {signUpStep === 4 && (
                  <PlanSelectionStep
                    lang={lang}
                    loading={loading}
                    defaultPlanId={selectedPlanId || undefined}
                    onSelectPlan={handleSelectPlan}
                    onSkip={() => navigate("/dashboard")}
                  />
                )}

              </div>
            )}


          </div>
                    </div>
      </main>

      {/* FOOTER */}
      <PolicyDialog type={reviewPolicy} documents={policies} error={policyError} onClose={() => setReviewPolicy(null)} />
      {!plainLayout ? (
        <footer className="w-full px-5 py-2 text-center text-[10px] text-neutral-400 relative z-10">
          LoyaltyBridge • Multi-tier Member Loyalty & Reward Architecture
        </footer>
      ) : (
        <footer className="w-full px-5 py-6 flex items-center justify-center gap-3 text-xs text-neutral-700 relative z-10">
          <Link to="/terms" className="underline underline-offset-2 hover:text-neutral-900">{t.terms}</Link>
          <span className="h-3 w-px bg-neutral-500/50" />
          <Link to="/privacy" className="underline underline-offset-2 hover:text-neutral-900">{t.privacy}</Link>
        </footer>
      )}
    </div>
  );
}
